import { venueAccent, type VenueKey } from '@tira/design-tokens';
import type { PlayerPuck as SeatData } from '@tira/protocol';
import type { CSSProperties, JSX } from 'react';
import { useT } from '../i18n/index.js';
import { ChipCount } from './ChipCount.js';
import { Countdown } from './Countdown.js';
import { Icon } from './Icon.js';
import './PlayerPuck.css';

export type { SeatData };

export interface PlayerPuckProps {
  /** The seat's public projection, straight from the server snapshot. */
  puck: SeatData;
  /** True while this seat is on the clock; drives the turn ring. */
  turn?: boolean;
  /** Epoch ms this seat's turn expires — straight from the server. */
  deadline?: number | null;
  turnMs?: number;
  /** The local player's own seat. */
  you?: boolean;
  /** Venue accent thread for the active ring. */
  venue?: VenueKey;
  className?: string;
}

export function PlayerPuck({
  puck: seat,
  turn = false,
  deadline = null,
  turnMs = 15_000,
  you = false,
  venue,
  className,
}: PlayerPuckProps): JSX.Element {
  const t = useT();
  const ghost = !seat.connected;
  const classes = ['puck'];
  if (turn) classes.push('puck--active');
  if (you) classes.push('puck--you');
  if (ghost) classes.push('puck--ghost');
  if (className !== undefined) classes.push(className);

  const style =
    venue === undefined ? undefined : ({ '--puck-accent': venueAccent[venue] } as CSSProperties);

  return (
    <div className={classes.join(' ')} style={style}>
      <div className="puck__seat">
        {turn ? (
          <Countdown
            className="puck__ring"
            deadline={deadline}
            turnMs={turnMs}
            size={48}
            strokeWidth={7}
            label={t('ui.puck.turn', { who: seat.handle })}
          />
        ) : null}
        <span className="puck__avatar" aria-hidden="true">
          {initial(seat.handle)}
        </span>
      </div>

      <div className="puck__body">
        <span className="puck__name">
          <span className="puck__handle">{seat.handle}</span>
          {/* Bots are always labeled — PRD §8.1. Do not make this conditional on anything else. */}
          {seat.isBot ? <span className="puck__ai">{t('ui.puck.ai')}</span> : null}
          {you ? <span className="pill">{t('ui.puck.you')}</span> : null}
        </span>
        {ghost ? (
          <span className="puck__status">
            <Icon name="signal" size={11} />
            {t('ui.puck.dropped')}
          </span>
        ) : (
          <ChipCount value={seat.chips} size="sm" />
        )}
      </div>
    </div>
  );
}

function initial(handle: string): string {
  return (handle.trim()[0] ?? '?').toUpperCase();
}
